import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar, Spinner, Modal, Button, Form } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import Badge from '../base/Badge';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import SearchBox from '../common/SearchBox';
import FilterTab from '../common/FilterTab';
import ProductColorDot from '../common/ProductColorDot';
import LengthSplit from '../common/LengthSplit';
import { splitOf } from '../../helpers/lengthSplit';
import TokenMapTable from '../project-management/TokenMapTable';
import { fetchProductAllocation } from '../../features/orders/ordersApi';
import { useJobs } from '../../features/jobs/hooks/useJobs';
import { gySuffix } from '../../helpers/productLabel';
import { useOrders, useOpenLines } from '../../features/orders/useOrders';
import { http } from '../../features/jobs/api/http.js';
import TableExpandButton from '../common/TableExpandButton';
import {
  fusedPhasesOf,
  phaseSegmentLabel
} from '../../helpers/tandemPhases';
import {
  FULL_TABLE_ROUTES,
  FULL_TABLE_PAGE_SIZE
} from '../../pages/full-table/routes';

// canonical phase names -> labels (English), same style as OrderTraceability
const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  exterior_sheat: 'Exterior Sheath',
  armouring: 'Armouring',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const PHASE_SHORT = {
  unwinding: 'Unwind',
  pre_stranding: 'Pre-str',
  conductor_isolation: 'Isol',
  rope: 'Rope',
  interior_sheat: 'Inner',
  exterior_sheat: 'Outer',
  armouring: 'Armour',
  coiling: 'Coil',
  rewinding: 'Rewind'
};
const phaseLabel = n => PHASE_LABEL[n] || n;
const phaseShort = n => PHASE_SHORT[n] || phaseLabel(n);

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const up = v => String(v || '').trim().toUpperCase();
const fmt = n => Number(n || 0).toLocaleString();

const statusBg = s => {
  const u = up(s);
  if (u === 'ACTIVE' || u === 'RUNNING') return 'success';
  if (u === 'PENDING') return 'warning';
  if (u === 'COMPLETED' || u === 'DONE') return 'info';
  return 'secondary';
};

const phaseOrderOf = job => {
  const fg = Array.isArray(job?.phasesForGant) ? job.phasesForGant : [];
  return fg.length ? fg : Object.keys(job?.phases || {});
};


const PhaseStepper = ({ order = [], phases = {}, currentIdx = -1, fused = {} }) => (
  <div className="d-flex flex-wrap align-items-center gap-1">
    {order.map((name, i) => {
      const prog = num(phases?.[name]?.progress);
      const done = prog >= 100;
      const current = i === currentIdx;
      const together = fused[name];

      let cls = 'bg-light text-muted border';
      if (done) cls = 'bg-success-subtle text-success border border-success';
      else if (current)
        cls = 'bg-info-subtle text-info border border-info fw-bold';

      const suffix = done ? ' ✓' : prog > 0 ? ` ${prog}%` : '';

      return (
        <React.Fragment key={name}>
          {i > 0 && <span className="text-300">›</span>}
          <span
            className={`px-2 py-1 rounded ${cls}`}
            style={{ fontSize: 10 }}
            title={together ? `${phaseSegmentLabel(name, fused, phaseLabel)} — extruded together in one pass` : undefined}
          >
            {phaseSegmentLabel(name, fused, phaseShort)}
            {suffix}
          </span>
        </React.Fragment>
      );
    })}
  </div>
);

// does a stock job match an open order line (same product)?
const jobMatchesLine = (row, line) => {
  const p = line?.product || {};
  return (
    up(row.name) === up(p.cableName) &&
    num(row.numberOfConductors) === num(p.numberOfConductors) &&
    num(row.section) === num(p.section) &&
    (!row.color || !p.color || up(row.color) === up(p.color))
  );
};

const ReserveModal = ({ show, onHide, row, openLines, onReserved, product }) => {
  const queryClient = useQueryClient();
  const [metersByLine, setMetersByLine] = useState({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const matches = useMemo(() => {
    if (!row) return [];
    return (openLines || [])
      .filter(l => num(l.remainingToPlan) > 0 && jobMatchesLine(row, l))
      .sort((a, b) =>
        String(a.dueDate || '9999').localeCompare(String(b.dueDate || '9999'))
      );
  }, [row, openLines]);

  if (!row) return null;
  const stockLeft = num(row.stockMeters);

  const doReserve = async line => {
    setMsg(null);
    const requested =
      metersByLine[line.lineId] != null
        ? num(metersByLine[line.lineId])
        : Math.min(stockLeft, num(line.remainingToPlan));
    if (requested <= 0) {
      setMsg({ type: 'danger', text: 'Enter a length > 0' });
      return;
    }
    setBusy(line.lineId);
    try {
      const res = await http.post(`/api/redis/jobs/${row.jobId}/reserve`, {
        orderId: line.orderId,
        lineId: line.lineId,
        meters: requested
      });
      const reserved = res?.data?.reserved ?? requested;
      setMsg({
        type: 'success',
        text: `Reserved ${fmt(reserved)} m to ${line.orderCode} (${line.client}).`
      });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (typeof onReserved === 'function') onReserved();
    } catch (e) {
      setMsg({
        type: 'danger',
        text: e?.response?.data?.error || 'Reservation failed'
      });
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          Reserve stock → order
          <span className="text-muted fs-9 ms-2">
            {row.productLabel} · {fmt(stockLeft)} m free
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {msg && (
          <div className={`alert alert-${msg.type} py-2 fs-9`}>{msg.text}</div>
        )}
        {/* What the product actually IS, before deciding what to promise of it.
            Sales reserves metres; the floor holds drums, and whether 3,000 m of
            those metres can come off as one continuous length is a question the
            metre figure cannot answer. Shown here rather than left to a phone
            call to production. */}
        {product?.chains?.length > 0 && (
          <div className="mb-3 pb-3 border-bottom">
            <TokenMapTable job={product} />
          </div>
        )}

        {!matches.length ? (
          <p className="text-muted mb-0">
            No open orders for this product. Stock stays unreserved.
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle fs-9 mb-0">
              <thead>
                <tr className="text-700">
                  <th>Order</th>
                  <th>Client</th>
                  <th>Due</th>
                  <th className="text-end">Remaining</th>
                  <th style={{ width: 130 }}>Reserve (m)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {matches.map(l => {
                  const defaultM = Math.min(stockLeft, num(l.remainingToPlan));
                  return (
                    <tr key={l.lineId}>
                      <td className="fw-semibold">{l.orderCode}</td>
                      <td>{l.client}</td>
                      <td className="text-700">{l.dueDate || '—'}</td>
                      <td className="text-end">{fmt(l.remainingToPlan)} m</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          max={defaultM}
                          value={
                            metersByLine[l.lineId] != null
                              ? metersByLine[l.lineId]
                              : defaultM
                          }
                          onChange={e =>
                            setMetersByLine(s => ({
                              ...s,
                              [l.lineId]: e.target.value
                            }))
                          }
                        />
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy === l.lineId || stockLeft <= 0}
                          onClick={() => doReserve(l)}
                        >
                          {busy === l.lineId ? '…' : 'Reserve'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="phoenix-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// `fullPage` = rendered on its own dedicated route rather than inside the card
// at the bottom of Order Monitor, so it gets a taller page of jobs.
const ProductionJobsTable = ({ fullPage = false }) => {
  const { data: jobs = [], isLoading } = useJobs();
  const { data: ordersData } = useOrders();
  const { data: openLinesData } = useOpenLines();
  const openLines = useMemo(
    () => (Array.isArray(openLinesData) ? openLinesData : []),
    [openLinesData]
  );

  // orderId -> client name
  const clientMap = useMemo(() => {
    const m = {};
    (Array.isArray(ordersData) ? ordersData : []).forEach(o => {
      if (o?.orderId) m[o.orderId] = o.client;
    });
    return m;
  }, [ordersData]);

  const rows = useMemo(() => {
    return (jobs || []).map(job => {
      const allocs = Array.isArray(job?.allocations) ? job.allocations : [];
      const allocatedToOrders = allocs.reduce(
        (s, a) => s + num(a?.allocatedMeters),
        0
      );
      const planned = num(job?.plannedLength);
      const stockMeters = Math.max(0, planned - allocatedToOrders);

      let destination = 'STOCK';
      if (allocatedToOrders > 0 && stockMeters > 0) destination = 'MIXED';
      else if (allocatedToOrders > 0) destination = 'CLIENT';

      const clients = [
        ...new Set(allocs.map(a => clientMap[a?.orderId]).filter(Boolean))
      ];
      // per-client launched meters (an order launch can cover several clients)
      const byClient = new Map();
      for (const a of allocs) {
        const client = clientMap[a?.orderId];
        if (!client) continue;
        byClient.set(client, (byClient.get(client) || 0) + num(a?.allocatedMeters));
      }
      const clientBreakdown = [...byClient.entries()].map(([client, meters]) => ({
        client,
        meters
      }));

      const order = phaseOrderOf(job);
      const curIdx = order.findIndex(
        n => num(job?.phases?.[n]?.progress) < 100
      );

      return {
        jobId: job.jobId,
        productLabel: job.productLabel || job.name,
        name: job.name,
        numberOfConductors: job.numberOfConductors,
        section: job.section,
        greenYellowSection: job.greenYellowSection ?? null,
        color: job.color,
        // product (sheath/lot) colour source — the ProductColorDot reads
        // source.launch.productKey, same as the project-monitor projects table.
        source: job.source,
        productColor: job.productColor,
        plannedLength: planned,
        status: job.status,
        progress: num(job.progress),
        phasesObj: job.phases || {},
        fusedPhases: fusedPhasesOf(job),
        phaseOrder: order,
        currentIdx: curIdx,
        currentPhase: curIdx === -1 ? null : order[curIdx],
        destination,
        clients,
        clientBreakdown,
        allocatedToOrders,
        stockMeters
      };
    });
  }, [jobs, clientMap]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [reserveRow, setReserveRow] = useState(null);

  /**
   * The token structure of every launched product, in one read.
   *
   * The same endpoint the production side uses. Fetched for the table rather
   * than per modal because the "longest piece" column needs it for every row —
   * and that column is the point: it says whether a drum of a given length is
   * possible at all, which the metre totals beside it cannot.
   */
  const { data: allocation } = useQuery({
    queryKey: ['product-allocation', null],
    queryFn: () => fetchProductAllocation(null)
  });

  const structureByJob = useMemo(() => {
    const m = new Map();
    for (const p of allocation?.products || []) m.set(String(p.jobId), p);
    return m;
  }, [allocation]);

  const matchingCount = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (r.stockMeters <= 0) {
        m[r.jobId] = 0;
        return;
      }
      m[r.jobId] = openLines.filter(
        l => num(l.remainingToPlan) > 0 && jobMatchesLine(r, l)
      ).length;
    });
    return m;
  }, [rows, openLines]);

  const tabItems = useMemo(() => {
    const c = { all: rows.length, CLIENT: 0, STOCK: 0, MIXED: 0 };
    rows.forEach(r => {
      if (r.destination === 'CLIENT') c.CLIENT++;
      else if (r.destination === 'STOCK') c.STOCK++;
      else if (r.destination === 'MIXED') c.MIXED++;
    });
    return [
      { label: 'All', value: 'all', count: c.all },
      { label: 'For client', value: 'CLIENT', count: c.CLIENT },
      { label: 'Stock', value: 'STOCK', count: c.STOCK },
      { label: 'Mixed', value: 'MIXED', count: c.MIXED }
    ];
  }, [rows]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (statusFilter !== 'all') {
      out = out.filter(r => r.destination === statusFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      out = out.filter(
        r =>
          (r.productLabel || '').toLowerCase().includes(q) ||
          (r.currentPhase || '').toLowerCase().includes(q) ||
          (r.clients.join(' ') || '').toLowerCase().includes(q) ||
          String(r.numberOfConductors || '').includes(q) ||
          String(r.section || '').includes(q)
      );
    }
    return out;
  }, [rows, statusFilter, searchText]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productLabel',
        header: 'Product',
        cell: ({ row: { original } }) => (
          <Link
            to={`/apps/project-management/project-details/${original.jobId}`}
            className="text-decoration-none"
          >
            <div className="d-flex flex-column">
              <span className="fw-semibold fs-9 d-inline-flex align-items-center gap-1">
                <ProductColorDot row={original} />
                {original.productLabel}
              </span>
              <span className="text-700 fs-10">
                {original.numberOfConductors}×{original.section}
                {gySuffix(original.section, original.greenYellowSection)} mm²
              </span>
            </div>
          </Link>
        ),
        meta: { headerProps: { style: { minWidth: 150 } } }
      },
      {
        id: 'split',
        header: 'Order / free',
        accessorFn: row => splitOf(row).toStock,
        // Destination says WHO the cable is for; this says HOW MUCH is still
        // unspoken for, which is the number somebody hunting for reservable
        // metres is actually after.
        cell: ({ row: { original } }) => (
          <LengthSplit job={original} showTotal={false} />
        ),
        meta: { headerProps: { style: { minWidth: 140 } } }
      },
      {
        accessorKey: 'destination',
        header: 'Destination',
        cell: ({ row: { original } }) => {
          const breakdown = original.clientBreakdown || [];
          const stock = num(original.stockMeters);
          if (!breakdown.length && stock <= 0) {
            return <span className="text-500">—</span>;
          }
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              {breakdown.map((b, i) => (
                <Badge
                  key={i}
                  bg="primary"
                  variant="phoenix"
                  className="fs-10"
                  title={`${b.client}: ${fmt(b.meters)} m allocated for this client`}
                >
                  {String(b.client).toUpperCase()} ·{' '}
                  <span className="fw-bold">{fmt(b.meters)} m</span>
                </Badge>
              ))}
              {stock > 0 && (
                <Badge
                  bg="success"
                  variant="phoenix"
                  className="fs-10"
                  title={`${fmt(stock)} m for stock`}
                >
                  STOCK · <span className="fw-bold">{fmt(stock)} m</span>
                </Badge>
              )}
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 160 } } }
      },
      {
        accessorKey: 'plannedLength',
        header: 'Length',
        cell: ({ row: { original } }) => (
          <span className="fw-semibold">{fmt(original.plannedLength)} m</span>
        ),
        meta: {
          cellProps: { className: 'text-end' },
          headerProps: { style: { width: '9%' }, className: 'text-end' }
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row: { original } }) => (
          <Badge
            bg={statusBg(original.status)}
            variant="phoenix"
            className="fs-10"
          >
            {up(original.status) || 'PENDING'}
          </Badge>
        ),
        meta: { headerProps: { style: { width: '9%' } } }
      },
      {
        accessorKey: 'currentPhase',
        header: 'Current Phase',
        cell: ({ row: { original } }) =>
          original.currentPhase ? (
            <Badge bg="primary" variant="phoenix" className="fs-10">
              {phaseLabel(original.currentPhase)}
            </Badge>
          ) : (
            <Badge bg="success" variant="phoenix" className="fs-10">
              Done
            </Badge>
          ),
        meta: { headerProps: { style: { width: '11%' } } }
      },
      {
        accessorKey: 'phaseOrder',
        header: 'Phases',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <PhaseStepper
            order={original.phaseOrder}
            phases={original.phasesObj}
            currentIdx={original.currentIdx}
            fused={original.fusedPhases}
          />
        ),
        meta: {
          cellProps: { className: 'py-2' },
          headerProps: { style: { minWidth: 220 } }
        }
      },
      {
        accessorKey: 'progress',
        header: 'Progress',
        cell: ({ row: { original } }) => {
          const pct = num(original.progress);
          return (
            <div className="d-flex align-items-center gap-2">
              <ProgressBar
                now={pct}
                style={{ height: 5, flex: 1 }}
                variant={
                  pct >= 100 ? 'success' : pct > 0 ? 'primary' : 'secondary'
                }
              />
              <span className="fs-10 text-700" style={{ minWidth: 28 }}>
                {pct}%
              </span>
            </div>
          );
        },
        meta: { headerProps: { style: { width: '11%' } } }
      },
      {
        id: 'longest',
        header: 'Longest piece',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const p = structureByJob.get(String(original.jobId));
          if (!p) return <span className="text-300">—</span>;
          const pieces = (p.pieces || []).filter(x => x.free > 0).length;
          return (
            <div className="d-flex flex-column align-items-end">
              <span
                className={p.longestPiece > 0 ? 'fw-semibold' : 'text-700'}
              >
                {fmt(p.longestPiece)} m
              </span>
              {/* Free metres say how MUCH; this says how LONG the biggest
                  continuous run is, and only the second decides whether a drum
                  of a given length can be cut at all. */}
              {p.freeMeters > p.longestPiece && (
                <span className="text-700 fs-10 white-space-nowrap">
                  in {pieces} pieces
                </span>
              )}
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: 120 }, className: 'text-end' },
          cellProps: { className: 'text-end' }
        }
      },
      {
        id: 'reserve',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          if (original.stockMeters <= 0)
            return <span className="text-300">—</span>;
          const n = matchingCount[original.jobId] || 0;
          return (
            <Button
              size="sm"
              variant={n > 0 ? 'phoenix-primary' : 'phoenix-secondary'}
              disabled={n === 0}
              onClick={() => setReserveRow(original)}
              title={
                n > 0
                  ? `${n} matching open order(s)`
                  : 'No open orders for this product'
              }
            >
              Reserve{n > 0 ? ` (${n})` : ''}
            </Button>
          );
        },
        meta: {
          headerProps: { style: { width: 110 }, className: 'text-center' },
          cellProps: { className: 'text-center' }
        }
      }
    ],
    [matchingCount, structureByJob]
  );

  const table = useAdvanceTable({
    data: filteredRows,
    columns,
    pageSize: fullPage ? FULL_TABLE_PAGE_SIZE.productionJobs : 8,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" size="sm" className="me-2" /> Loading jobs...
      </div>
    );
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-2">
          <FilterTab
            tabItems={tabItems}
            activeKey={statusFilter}
            onSelect={setStatusFilter}
          />
          <div className="d-flex align-items-center gap-2">
            <SearchBox
              placeholder="Search product, client, phase..."
              onChange={e => setSearchText(e.target.value || '')}
            />
            {!fullPage && (
              <TableExpandButton to={FULL_TABLE_ROUTES.productionJobs} />
            )}
          </div>
        </div>

        {!rows.length ? (
          <div className="text-muted text-center py-4">No jobs found.</div>
        ) : filteredRows.length === 0 ? (
          <div className="text-700 py-3 text-center fs-9">No matching jobs</div>
        ) : (
          <div className="border-bottom">
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table border-top border-200 fs-9'
              }}
            />
            <AdvanceTableFooter pagination className="py-3" />
          </div>
        )}
      </AdvanceTableProvider>

      <ReserveModal
        product={reserveRow ? structureByJob.get(String(reserveRow.jobId)) : null}
        show={!!reserveRow}
        row={reserveRow}
        openLines={openLines}
        onHide={() => setReserveRow(null)}
        onReserved={() => setReserveRow(null)}
      />
    </>
  );
};

export default ProductionJobsTable;
